import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { getBackendUrl } from '../utils/config';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import {
  Utensils,
  LayoutDashboard,
  TrendingUp,
  Wallet,
  Package,
  Users,
  Heart,
  Calendar,
  Megaphone,
  LineChart,
  Target,
  CheckSquare,
  Bell,
  Menu,
  X,
  LogOut,
  Settings,
  ChevronRight,
  ChevronLeft,
  Search,
  PanelLeftClose,
  PanelLeft,
  Crown,
  Shield
} from 'lucide-react';

const navItems = [
  { 
    path: '/company/dashboard', 
    label: 'Dashboard', 
    icon: LayoutDashboard, 
    permission: null 
  },
  { 
    path: '/company/omzet-verkoop', 
    label: 'Omzet & Verkoop', 
    icon: TrendingUp, 
    permission: 'omzet_verkoop' 
  },
  { 
    path: '/company/kosten-marges', 
    label: 'Kosten & Marges', 
    icon: Wallet, 
    permission: 'kosten_marges' 
  },
  { 
    path: '/company/voorraad-inkoop', 
    label: 'Voorraad & Inkoop', 
    icon: Package, 
    permission: 'voorraad_inkoop' 
  },
  { 
    path: '/company/personeel-planning', 
    label: 'Personeel & Planning', 
    icon: Users, 
    permission: 'personeel_planning' 
  },
  { 
    path: '/company/gast-beleving', 
    label: 'Gast & Beleving', 
    icon: Heart, 
    permission: 'gast_beleving' 
  },
  { 
    path: '/company/reserveringen-bezetting', 
    label: 'Reserveringen & Bezetting', 
    icon: Calendar, 
    permission: 'reserveringen_bezetting' 
  },
  { 
    path: '/company/marketing-acties', 
    label: 'Marketing & Acties', 
    icon: Megaphone, 
    permission: 'marketing_acties' 
  },
  { 
    path: '/company/trends-forecasting', 
    label: 'Trends & Forecasting', 
    icon: LineChart, 
    permission: 'trends_forecasting' 
  },
  { 
    path: '/company/kpi-management', 
    label: 'KPI Management', 
    icon: Target, 
    permission: 'kpi_management' 
  },
  { 
    path: '/company/doelen-structuur', 
    label: 'Doelen & Structuur', 
    icon: CheckSquare, 
    permission: 'doelen_structuur' 
  },
  { 
    path: '/company/acties-evaluatie', 
    label: 'Acties & Evaluatie', 
    icon: Bell, 
    permission: 'acties_evaluatie' 
  },
  { 
    path: '/company/research', 
    label: 'Research', 
    icon: Search, 
    permission: null 
  }
];

// Admin-only nav items
const adminNavItems = [
  {
    path: '/admin/dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    permission: null
  },
  {
    path: '/admin/companies',
    label: 'Bedrijven',
    icon: Users,
    permission: null
  },
  {
    path: '/admin/subscriptions',
    label: 'Subscriptions',
    icon: Crown,
    permission: null
  },
  {
    path: '/admin/settings',
    label: 'Instellingen',
    icon: Settings,
    permission: null
  }
];

const Sidebar = () => {
  const { user, logout, hasPermission } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar-collapsed');
    return saved === 'true';
  });
  const [urgentCount, setUrgentCount] = useState(0);
  const location = useLocation();

  const API_URL = getBackendUrl();

  // Fetch urgent tasks count when user is logged in
  useEffect(() => {
    let isMounted = true;
    
    const fetchUrgentTasks = async () => {
      try {
        const token = localStorage.getItem('horeca_token');
        if (!token || !user) {
          return;
        }
        
        const res = await axios.get(`${API_URL}/api/tasks`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (!isMounted) return;
        
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        // Count high priority tasks + overdue tasks
        const urgent = res.data.filter(t => 
          t.status === 'open' && (
            t.priority === 'hoog' || 
            t.priority === 'high' || 
            (t.due_date && new Date(t.due_date) < today)
          )
        );
        
        setUrgentCount(urgent.length);
      } catch (err) {
        console.error('[Sidebar] Error fetching tasks:', err.message);
      }
    };

    if (user) {
      // Small delay to ensure token is saved
      const timer = setTimeout(fetchUrgentTasks, 500);
      // Also refresh every 5 minutes
      const interval = setInterval(fetchUrgentTasks, 5 * 60 * 1000);
      return () => {
        isMounted = false;
        clearTimeout(timer);
        clearInterval(interval);
      };
    }
  }, [API_URL, user, location.pathname]);

  // Save collapsed state to localStorage and dispatch event
  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', collapsed);
    // Dispatch custom event for same-tab updates
    window.dispatchEvent(new CustomEvent('sidebar-collapse-change', { detail: collapsed }));
  }, [collapsed]);

  const filteredNavItems = user?.role === 'admin' 
    ? [] // Admin users don't see company nav items
    : navItems.filter(item => !item.permission || hasPermission(item.permission));

  // Filter admin items - only show for admin users
  const filteredAdminItems = user?.role === 'admin' ? adminNavItems : [];

  const toggleCollapse = () => {
    setCollapsed(!collapsed);
  };

  const SidebarContent = () => (
    <>
      {/* Logo - with safe-area padding for PWA */}
      <div 
        className={`p-4 border-b border-[#2A4208] ${collapsed ? 'px-2' : ''}`}
        style={{ paddingTop: 'max(1rem, env(safe-area-inset-top, 0px) + 0.5rem)' }}
      >
        <div className={`flex items-center ${collapsed ? 'justify-center' : 'gap-3'}`}>
          <div className="w-10 h-10 rounded-lg bg-[#C2410C] flex items-center justify-center flex-shrink-0">
            <Utensils className="w-5 h-5 text-white" />
          </div>
          {!collapsed && (
            <div className="overflow-hidden">
              <h1 className="text-lg font-bold text-white whitespace-nowrap" style={{ fontFamily: 'Manrope' }}>
                Horeca
              </h1>
              <p className="text-xs text-[#F5F5F4]/60">Dashboard</p>
            </div>
          )}
        </div>
      </div>

      {/* Collapse Toggle Button - Desktop only */}
      <div className={`hidden lg:flex ${collapsed ? 'justify-center' : 'justify-end'} px-2 py-2`}>
        <Button
          variant="ghost"
          size="sm"
          onClick={toggleCollapse}
          className="text-[#F5F5F4]/60 hover:text-[#F5F5F4] hover:bg-[#2A4208]"
          data-testid="sidebar-collapse-btn"
        >
          {collapsed ? (
            <PanelLeft className="w-5 h-5" />
          ) : (
            <PanelLeftClose className="w-5 h-5" />
          )}
        </Button>
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 py-2">
        <TooltipProvider delayDuration={0}>
          <nav className="space-y-1">
            {/* Company Navigation Items (only for non-admin users) */}
            {filteredNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              
              const navLink = (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileOpen(false)}
                  className={`sidebar-item ${isActive ? 'active' : ''} ${collapsed ? 'collapsed justify-center px-0 mx-0' : ''}`}
                  data-testid={`nav-${item.path.replace('/', '')}`}
                >
                  <div className="relative">
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    {/* Show badge for Doelen & Structuur when there are urgent tasks */}
                    {item.path === '/doelen-structuur' && urgentCount > 0 && collapsed && (
                      <span className="absolute -top-1 -right-1 h-4 min-w-[16px] px-1 bg-rose-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                        {urgentCount > 9 ? '9+' : urgentCount}
                      </span>
                    )}
                  </div>
                  {!collapsed && (
                    <>
                      <span className="text-sm font-medium truncate flex-1">{item.label}</span>
                      {/* Show badge for Doelen & Structuur */}
                      {item.path === '/doelen-structuur' && urgentCount > 0 && (
                        <span className="h-5 min-w-[20px] px-1.5 bg-rose-500 text-white text-xs font-bold rounded-full flex items-center justify-center mr-1">
                          {urgentCount > 9 ? '9+' : urgentCount}
                        </span>
                      )}
                      {isActive && <ChevronRight className="w-4 h-4 flex-shrink-0" />}
                    </>
                  )}
                </NavLink>
              );

              if (collapsed) {
                return (
                  <Tooltip key={item.path}>
                    <TooltipTrigger asChild>
                      {navLink}
                    </TooltipTrigger>
                    <TooltipContent side="right" className="bg-[#1A2E05] text-white border-[#2A4208]">
                      {item.label}
                    </TooltipContent>
                  </Tooltip>
                );
              }

              return navLink;
            })}

            {/* Admin Navigation Items (only for admin users) */}
            {filteredAdminItems.length > 0 && (
              <>
                {filteredAdminItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  
                  const adminLink = (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      onClick={() => setMobileOpen(false)}
                      className={`sidebar-item ${isActive ? 'active' : ''} ${collapsed ? 'collapsed justify-center px-0 mx-0' : ''}`}
                      data-testid={`nav-admin-${item.path.split('/').pop()}`}
                    >
                      <Icon className="w-5 h-5 flex-shrink-0" />
                      {!collapsed && (
                        <>
                          <span className="text-sm font-medium truncate flex-1">{item.label}</span>
                          {isActive && <ChevronRight className="w-4 h-4 flex-shrink-0" />}
                        </>
                      )}
                    </NavLink>
                  );

                  if (collapsed) {
                    return (
                      <Tooltip key={item.path}>
                        <TooltipTrigger asChild>
                          {adminLink}
                        </TooltipTrigger>
                        <TooltipContent side="right" className="bg-[#1A2E05] text-white border-[#2A4208]">
                          {item.label}
                        </TooltipContent>
                      </Tooltip>
                    );
                  }

                  return adminLink;
                })}
              </>
            )}
          </nav>
        </TooltipProvider>
      </ScrollArea>

      {/* User section */}
      <div className={`p-4 border-t border-[#2A4208] ${collapsed ? 'p-2' : ''}`}>
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger 
            className={`flex items-center w-full p-2 rounded-lg hover:bg-[#2A4208] active:bg-[#2A4208] transition-colors cursor-pointer touch-manipulation ${collapsed ? 'justify-center' : 'gap-3'}`}
            data-testid="user-menu-trigger"
          >
            <Avatar className="w-9 h-9 flex-shrink-0 pointer-events-none">
              <AvatarFallback className="bg-[#C2410C] text-white text-sm">
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </AvatarFallback>
            </Avatar>
            {!collapsed && (
              <div className="flex-1 text-left overflow-hidden pointer-events-none">
                <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                <p className="text-xs text-[#F5F5F4]/60 capitalize">{user?.role}</p>
              </div>
            )}
          </DropdownMenuTrigger>
          <DropdownMenuContent 
            align={collapsed ? "center" : "end"} 
            side={collapsed ? "right" : "top"} 
            className="w-56 z-[100]"
            sideOffset={8}
          >
            {user?.role !== 'admin' && (
              <DropdownMenuItem asChild>
                <NavLink to="/company/instellingen" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 cursor-pointer">
                  <Settings className="w-4 h-4" />
                  Instellingen
                </NavLink>
              </DropdownMenuItem>
            )}
            {user?.role !== 'admin' && <DropdownMenuSeparator />}
            <DropdownMenuItem 
              onClick={logout} 
              className="text-rose-600 cursor-pointer"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Uitloggen
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile menu button - only show when menu is closed */}
      {!mobileOpen && (
        <div 
          className="fixed top-14 left-4 z-[60] lg:hidden" 
          style={{ top: 'max(3.5rem, env(safe-area-inset-top, 0px) + 1rem)' }}
        >
          <div className="relative">
            <Button
              variant="ghost"
              size="icon"
              className="bg-white shadow-md rounded-full border h-12 w-12"
              onClick={() => setMobileOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-6 h-6" />
            </Button>
            {/* Urgent tasks badge */}
            {urgentCount > 0 && (
              <span 
                className="absolute -top-1 -right-1 h-5 min-w-[20px] px-1.5 bg-rose-500 text-white text-xs font-bold rounded-full flex items-center justify-center shadow-md pointer-events-none"
                data-testid="urgent-badge"
                style={{ animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }}
              >
                {urgentCount > 9 ? '9+' : urgentCount}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Mobile overlay */}
      {mobileOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-[55] lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile close button - fixed positioned, above everything */}
      {mobileOpen && (
        <button
          onClick={() => setMobileOpen(false)}
          className="fixed text-white bg-[#C2410C] hover:bg-[#9a3309] h-5 w-5 z-[70] lg:hidden rounded-full shadow-lg border border-white flex items-center justify-center"
          data-testid="mobile-close-btn"
          style={{ 
            top: 'max(0.75rem, env(safe-area-inset-top, 0px) + 0.5rem)',
            left: 'calc(16rem - 2rem)' // sidebar width (256px) minus button width plus margin
          }}
        >
          <X className="w-3 h-3" />
        </button>
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${mobileOpen ? 'open' : ''} ${collapsed ? 'sidebar-collapsed' : ''}`}>
        <SidebarContent />
      </aside>
    </>
  );
};

export default Sidebar;
